import {
    Button,
    ErrorPayload,
    HeartbeatResponse,
    IPIFY_API_URL,
    IPlatformApi,
    joinFsPath,
    LeaseMetadata,
    License,
    LicenseCode,
    LicenseError,
    LicenseFailure,
    LicenseResponse,
    LicenseSuccess,
    SelectSeatResponse,
    SuccessFlagResponse
} from '..';
import { APPWRITE_API } from './appwriteApi';
import { isLicenseResponse, toErrorPayload, isRecord, isSelectSeatResponse } from './helpers';
import { type ReleaseReason } from '../types/license-types';

// Note: LicenseService is designed to be used as a static singleton class and should not be instantiated multiple times.
// It must be initialized at the top in the App.tsx file with the platform API to function properly, but after that it can be used globally without needing to pass around an instance.
// The constructor is only used for initial platform API injection, and all methods/properties are static for global access and state management.
export class LicenseService {
    private static _seq: number = 0;
    private static _detectIpOnce?: Promise<void>;
    private static DEVICE_ID_FILE: string = 'deviceid';
    static platformApi: IPlatformApi;
    static detectedIp: string = '';
    static error: LicenseError | null = null;
    static licenses: License[] = [];
    static selectedLicenseId?: string;
    static userId: string = '';
    static sessionId: string = '';
    static inflight?: AbortController;
    static onNetworkChange?: (isOnline: boolean) => void;
    static onError?: (error: LicenseError | null) => void;
    static bypassed: boolean = false;

    // License refresh
    static refreshTimer: ReturnType<typeof setTimeout> | null = null;
    static refreshIntervalMs: number = 5 * 60_000; // 5 minutes
    static refreshJitterMs: number = 60_000; // ±1 minute
    static refreshBackoffMs: number = 0; // 0 minute
    static refreshGraceMs: number = 24 * 60 * 60_000; // 24 hours

    // Heartbeat related
    static retryTimeout: ReturnType<typeof setTimeout> | null = null;
    static heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
    static heartbeatIntervalSec: number = 5 * 60; // 5 minutes
    static heartbeatLicenseId: string | null = null;
    static heartbeatInFlight: Promise<void> | null = null;
    static listenersBound: boolean = false;
    static leaseExpiresAt?: number;
    static headers: Record<string, string> = {
        'content-type': 'application/json',
        'device-id': ''
    };

    static platformApiType: string;

    static readonly RETRY_TIMEOUT: number = 5000; // 5 seconds
    static readonly DEFAULT_HEARTBEAT_INTERVAL_SEC: number = 300; // 5 minutes

    constructor(platformApi: IPlatformApi, bypassed: boolean = false) {
        LicenseService.platformApi = platformApi;
        LicenseService.bypassed = bypassed;
        // See note at top of class about static usage
    }

    static configure(platformApiType: string): void {
        this.platformApiType = platformApiType;
    }

    private static ensureDetectedIpOnce(): Promise<void> {
        if (this.detectedIp) return Promise.resolve();
        if (!this._detectIpOnce) {
            this._detectIpOnce = (async () => {
                try {
                    const response: Response = await fetch(IPIFY_API_URL);
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    const { ip } = await response.json();
                    this.detectedIp = ip ?? '';
                } catch {
                    this.detectedIp = '';
                }
            })();
        }
        return this._detectIpOnce;
    }

    private static async fetchWithPrep(input: RequestInfo, init?: RequestInit): Promise<Response> {
        if (typeof window !== 'undefined') await this.ensureDetectedIpOnce();
        return fetch(input, init);
    }

    private static logResponseStatus(context: string, response: Response): void {
        console.debug(`[LicenseService]: ${context} response status: ${response.status}`);
    }

    private static summarizeLicense(license: License): Record<string, unknown> {
        return {
            licenseTier: license.licenseTier,
            displayName: license.displayName,
            seatAvailable: license.seatAvailable,
            activeSeats: license.activeSeats,
            seatCount: license.seatCount,
            expiresAt: license.expiresAt,
            expired: license.expired
        };
    }

    private static logLicenses(context: string, licenses: License[]): void {
        console.debug(
            `[LicenseService]: ${context} licenses (${licenses.length}):`,
            licenses.map((license) => this.summarizeLicense(license))
        );
    }

    private static isOfflineNetworkError(error: unknown): boolean {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
        const message: string = error instanceof Error ? error.message : String(error);
        return message.includes('Failed to fetch') || message.includes('ERR_INTERNET_DISCONNECTED');
    }

    private static async getDeviceIdFilePath(): Promise<string> {
        const resourcePath: string = await this.platformApi.getResourcesPath();
        return joinFsPath(resourcePath, '..', this.DEVICE_ID_FILE);
    }

    static async saveDeviceId(): Promise<boolean> {
        if (!this.headers['device-id']) {
            console.warn('[LicenseService]: No device ID available to save');
            return false;
        }
        try {
            const deviceId: string = this.headers['device-id'];
            const deviceIdFilePath: string = await this.getDeviceIdFilePath();
            const success: boolean = await this.platformApi.saveFileData(deviceIdFilePath, deviceId);

            if (success) {
                console.debug(
                    `[LicenseService]: Device ID (${deviceId}) saved to platform storage ${deviceIdFilePath}`
                );
                return true;
            }
            console.warn('[LicenseService]: Failed to save device ID to platform storage');
            return false;
        } catch (error: unknown) {
            console.warn(`[LicenseService]: ${error instanceof Error ? error.message : 'Unknown error'}`);
            return false;
        }
    }

    static async loadDeviceId(): Promise<boolean> {
        try {
            const deviceIdFilePath: string = await this.getDeviceIdFilePath();

            let deviceId: string = '';
            try {
                deviceId = (await this.platformApi.getFileData(deviceIdFilePath)).trim();
            } catch (error: unknown) {
                const errorMsg: string = error instanceof Error ? error.message : 'Unknown error';
                console.warn(`[LicenseService]: ${errorMsg}`);
                return false;
            }

            if (!deviceId) {
                console.warn(
                    '[LicenseService]: Device ID file is empty. This may be caused by a failed save operation or first run. Will attempt to get device ID from server response header if available.'
                );
                return false;
            }

            this.headers['device-id'] = deviceId;
            console.debug(`[LicenseService]: Device ID (${deviceId}) loaded from platform storage`);
            return true;
        } catch (error: unknown) {
            const errorMsg: string = error instanceof Error ? error.message : String(error);
            if (errorMsg.includes('no such file or directory')) {
                console.debug('[LicenseService]: Device ID file not found, this may be expected on first run');
                return false;
            }
            console.warn(`[LicenseService]: ${errorMsg}`);
            return false;
        }
    }

    static async getLicenses(): Promise<void> {
        // Ensure we actually have credentials to send
        if (!this.userId || !this.sessionId) {
            this.setError({
                title: 'Authentication required',
                code: LicenseCode.INVALID_CREDENTIALS,
                message: 'Please sign in to continue.',
                timestamp: new Date().toISOString(),
                status: 401,
                enabledButtons: { [Button.SIGN_IN]: true }
            });
            return;
        }

        if (this.inflight) this.inflight.abort(); // Cancel previous call to prevent stale overwrites

        const controller: AbortController = new AbortController();
        this.inflight = controller;
        const seq: number = ++this._seq;

        const loadedDeviceId: boolean = await this.loadDeviceId();

        if (!loadedDeviceId) {
            console.warn(
                '[LicenseService]:\n    Failed to load device ID from save file.\n    Getting new device ID from getLicenses response header if available.'
            );
        }

        try {
            const response: Response = await fetch(`${APPWRITE_API}/getlicenses`, {
                method: 'POST',
                headers: this.headers,
                credentials: 'include',
                body: JSON.stringify({
                    userId: this.userId,
                    sessionId: this.sessionId,
                    deviceId: this.headers['device-id']
                }),
                signal: controller.signal
            });
            this.logResponseStatus('getLicenses', response);

            let body: LicenseResponse;
            try {
                const json: unknown = await response.json();
                if (!isLicenseResponse(json)) throw new Error('Invalid license response shape');
                body = json;
            } catch (err) {
                this.setError({
                    title: 'Invalid Response',
                    code: LicenseCode.BAD_JSON,
                    message: 'Bad response from server',
                    timestamp: new Date().toISOString(),
                    status: response.status,
                    description: err instanceof Error ? err.message : 'Unknown error',
                    enabledButtons: { [Button.RETRY]: true, [Button.SIGN_IN]: true }
                });
                return; // Stop on bad JSON
            }

            if (!response.ok) {
                const errorPayload = !body.success ? toErrorPayload((body as LicenseFailure).error) : undefined;
                this.setError({
                    title: 'Connection Error',
                    status: response.status,
                    timestamp: response.headers.get('date') || new Date().toISOString(),
                    code: response.status || LicenseCode.HTTP_ERROR,
                    message: errorPayload?.message || 'HTTP request failed',
                    description: errorPayload?.description || '',
                    enabledButtons: { [Button.RETRY]: true, [Button.SIGN_IN]: true }
                });
                return;
            }
            if (!body.success) {
                const errPayload: ErrorPayload | undefined = toErrorPayload((body as LicenseFailure).error);
                this.setError({
                    status: response.status,
                    timestamp: response.headers.get('date') || new Date().toISOString(),
                    code: errPayload?.code || response.status || LicenseCode.HTTP_ERROR,
                    message: errPayload?.message || 'Failed to receive a valid response from server',
                    description: errPayload?.description || '',
                    enabledButtons: { [Button.RETRY]: true, [Button.LOGOUT]: true }
                });
                return;
            }

            if (seq !== this._seq || controller.signal.aborted) return; // Ignore if a newer request already started

            const successBody: LicenseSuccess = body;

            if (!loadedDeviceId) {
                const deviceId: string = response.headers.get('device-id') ?? '';
                console.warn('[LicenseService]: Getting new device ID', deviceId);
                await this.setDeviceId(deviceId);
            }

            this.setError(null);
            this.licenses = Array.isArray(successBody.licenses) ? successBody.licenses : [];
            this.logLicenses('getLicenses', this.licenses);
        } catch (error: unknown) {
            // Ignore if request was aborted or a newer request started
            if (seq !== this._seq || controller.signal.aborted) return;

            // Distinguish aborts from other network errors
            if (error instanceof DOMException && error.name === 'AbortError') return; // Request was intentionally aborted, not showing error

            if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                // Offline mode, keep silent in grace period
                return;
            }

            // Fallback generic network/error message
            const errPayload: ErrorPayload | undefined = toErrorPayload(error);
            const fallbackMessage: string =
                error instanceof Error ? error.message : typeof error === 'string' ? error : String(error);
            const statusCode: number =
                isRecord(error) && typeof (error as { status?: unknown }).status === 'number'
                    ? ((error as { status?: number }).status ?? 0)
                    : 0;
            this.setError({
                title: 'Network Error',
                message: errPayload?.message || 'Unable to contact the licensing server. Please try again later.',
                code: errPayload?.code || LicenseCode.NETWORK_ERROR,
                description: errPayload?.description || fallbackMessage,
                timestamp: new Date().toISOString(),
                enabledButtons: { [Button.RETRY]: true, [Button.SIGN_IN]: false },
                status: statusCode
            });
        } finally {
            if (this.inflight === controller) this.inflight = undefined;
        }
    }

    static validateSelectedLicense(): boolean {
        if (this.selectedLicenseId === '') return false;
        return this.licenses.some((license) => license.id === this.selectedLicenseId);
    }

    static async selectLicense(licenseId: string): Promise<boolean> {
        try {
            const response: Response = await this.fetchWithPrep(`${APPWRITE_API}/selectseat`, {
                method: 'POST',
                headers: this.headers,
                credentials: 'include',
                body: JSON.stringify({
                    userId: this.userId,
                    sessionId: this.sessionId,
                    licenseId,
                    deviceId: this.headers['device-id'],
                    detectedIp: this.detectedIp,
                    appVersion: window.LVGL_EDITOR_VERSION || 'unknown',
                    platform: window?.platform?.getHostPlatform() || 'unknown'
                })
            });
            this.logResponseStatus('selectLicense', response);

            const data: unknown = await response.json();
            const rawBody: SelectSeatResponse = data as SelectSeatResponse;
            if (!isSelectSeatResponse(rawBody)) {
                this.setError({
                    message: 'License selection failed',
                    code: LicenseCode.INVALID_RESPONSE,
                    description: 'Received unexpected response payload while selecting license',
                    timestamp: new Date().toISOString(),
                    status: response.status
                });
                return false;
            }

            if (!rawBody.success) {
                const errPayload: ErrorPayload | undefined = toErrorPayload(rawBody.error);
                this.setError({
                    message: errPayload?.message || 'License selection failed',
                    code: errPayload?.code || LicenseCode.SELECT_FAILED,
                    description: errPayload?.description || '',
                    timestamp: new Date().toISOString(),
                    status: response.status,
                    enabledButtons: { [Button.RETRY]: true, [Button.SELECT_LICENSE]: true }
                });
                return false;
            }

            console.info(`[LicenseService]: License validated against Appwrite: ${licenseId}`);
            this.selectedLicenseId = licenseId;

            const lease: LeaseMetadata | undefined = rawBody.lease;

            // Apply server hints
            const nextHeartbeat: number | undefined =
                typeof lease?.nextHeartbeatInSeconds === 'number' ? lease.nextHeartbeatInSeconds : undefined;
            if (Number.isFinite(nextHeartbeat) && nextHeartbeat && nextHeartbeat > 0) {
                this.heartbeatIntervalSec = nextHeartbeat;
            }
            const leaseExpiry: string | undefined = lease?.expiresAt || lease?.leaseExpiresAt || undefined;
            this.leaseExpiresAt = leaseExpiry ? Date.parse(leaseExpiry) : undefined;

            // Kick off initial heartbeat immediately
            await this.startHeartbeat(this.selectedLicenseId);
            return true;
        } catch (error: unknown) {
            if (this.isOfflineNetworkError(error)) return false;
            throw error;
        }
    }

    static setError(error: LicenseError | null) {
        this.error = error;
        this.onError?.(error);
    }

    static startRefreshLoop(cb?: () => Promise<void> | void): ReturnType<typeof setTimeout> | undefined {
        if (this.refreshTimer) return this.refreshTimer; // Already running

        const tick = async (callback?: () => Promise<void> | void): Promise<void> => {
            try {
                if (callback) await callback();
                else await this.getLicenses();

                this.refreshBackoffMs = 0;
            } catch {
                this.refreshBackoffMs = Math.min(
                    this.refreshBackoffMs ? this.refreshBackoffMs * 2 : 5_000,
                    60 * 60_000
                );
            } finally {
                const base: number = this.refreshIntervalMs + this.refreshBackoffMs;
                const jitter: number = Math.floor((Math.random() * 2 - 1) * this.refreshJitterMs);
                const delay: number = Math.max(15_000, base + jitter);
                this.refreshTimer = setTimeout(() => {
                    void tick(callback);
                }, delay);
            }
        };
        this.refreshTimer = setTimeout(() => {
            void tick(cb);
        }, 1_000); // Start after 1 second

        return this.refreshTimer;
    }

    static stopRefreshLoop(): void {
        if (this.refreshTimer) clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
        this.refreshBackoffMs = 0;
    }

    static scheduleRetryHeartbeat(): void {
        if (this.retryTimeout) return; // Already scheduled
        this.retryTimeout = setTimeout(() => {
            this.heartbeat();
        }, this.RETRY_TIMEOUT); // Retry after 5 seconds
    }

    static scheduleNextBeat(): void {
        if (!this.selectedLicenseId) {
            console.debug(`[Heartbeat]: no license selected, skipping heartbeat`);
            return; // Nothing to renew
        }
        if (this.heartbeatTimer) {
            console.debug('[Heartbeat]: clearing existing timer', {
                hbTimer: this.heartbeatTimer
            });
            clearTimeout(this.heartbeatTimer);
        }

        const delayMs = this.heartbeatIntervalSec * 1000;

        console.debug('[Heartbeat]: Schedule Next Heartbeat', {
            hbIntervalSec: this.heartbeatIntervalSec,
            delayMs,
            selectedLicenseId: this.selectedLicenseId,
            leaseExpiresAt: this.leaseExpiresAt ? new Date(this.leaseExpiresAt).toLocaleString() : undefined,
            nextHeartbeatAt: new Date(Date.now() + delayMs).toLocaleString()
        });
        this.heartbeatTimer = setTimeout(() => {
            console.debug('[Heartbeat]: heartbeat timer fired', { timestamp: Date.now() });
            void this.heartbeat();
        }, delayMs);
    }

    private static stopHeartbeat(): void {
        if (this.heartbeatTimer) clearTimeout(this.heartbeatTimer);
        this.heartbeatTimer = null;

        if (this.retryTimeout) clearTimeout(this.retryTimeout);
        this.retryTimeout = null;

        this.heartbeatLicenseId = null;
        this.heartbeatInFlight = null;
    }

    public static async startHeartbeat(licenseId: string): Promise<void> {
        if (this.heartbeatTimer && this.heartbeatLicenseId === licenseId) return;
        if (this.heartbeatInFlight) return this.heartbeatInFlight;

        this.heartbeatInFlight = (async () => {
            this.stopHeartbeat();

            this.heartbeatLicenseId = licenseId;
            this.selectedLicenseId = licenseId;

            await this.heartbeat();
        })();

        return this.heartbeatInFlight.finally(() => {
            this.heartbeatInFlight = null;
        });
    }

    public static async heartbeat(): Promise<void> {
        if (!this.selectedLicenseId) {
            console.debug(`[Heartbeat]: no license selected, skipping heartbeat`);
            return; // Nothing to renew
        }
        try {
            console.debug('[Heartbeat]: Sending heartbeat', {
                selectedLicenseId: this.selectedLicenseId,
                leaseExpiresAt: this.leaseExpiresAt ? new Date(this.leaseExpiresAt).toLocaleString() : undefined,
                timestamp: Date.now()
            });
            const response: Response = await this.fetchWithPrep(`${APPWRITE_API}/heartbeat`, {
                method: 'POST',
                headers: this.headers,
                credentials: 'include',
                body: JSON.stringify({
                    userId: this.userId,
                    sessionId: this.sessionId,
                    licenseId: this.selectedLicenseId,
                    deviceId: this.headers['device-id'],
                    detectedIp: this.detectedIp,
                    appVersion: window.LVGL_EDITOR_VERSION || 'unknown',
                    platform: window?.platform?.getHostPlatform() || 'unknown'
                })
            });
            this.logResponseStatus('heartbeat', response);
            const data: unknown = await response.json();
            const rawBody: HeartbeatResponse = data as HeartbeatResponse;
            if (!rawBody?.success) throw new Error(toErrorPayload(rawBody?.error)?.message || 'Heartbeat failed');

            const lease: LeaseMetadata | undefined = rawBody.lease;
            const nextHeartbeat: number | undefined =
                lease && typeof lease.nextHeartbeatInSeconds === 'number' ? lease.nextHeartbeatInSeconds : undefined;
            this.heartbeatIntervalSec =
                Number.isFinite(nextHeartbeat) && nextHeartbeat && nextHeartbeat > 0
                    ? nextHeartbeat
                    : this.DEFAULT_HEARTBEAT_INTERVAL_SEC;
            const leaseExpiry: string | undefined = lease?.leaseExpiresAt || lease?.expiresAt;
            if (leaseExpiry) this.leaseExpiresAt = Date.parse(leaseExpiry);

            this.scheduleNextBeat();
        } catch (error: unknown) {
            if (this.isOfflineNetworkError(error)) {
                console.debug('[Heartbeat]: offline/network issue, waiting for retry');
                this.scheduleRetryHeartbeat();
                return;
            }
            const errMessage = error instanceof Error ? error.message : String(error);
            console.error('[Heartbeat][Error]:', errMessage);

            if (
                (error instanceof Error && errMessage.includes('different device')) ||
                errMessage.includes('Seat In Use')
            ) {
                this.setError({
                    title: 'License Seat In Use',
                    message: 'Your license seat is being used on a different device.',
                    code: LicenseCode.SEAT_IN_USE_ON_OTHER_DEVICE,
                    timestamp: new Date().toISOString(),
                    status: 409,
                    enabledButtons: { [Button.RECLAIM]: true, [Button.SELECT_LICENSE]: true }
                });
            }

            this.scheduleRetryHeartbeat();

            if (this.leaseExpiresAt && Date.now() > this.leaseExpiresAt) {
                this.selectedLicenseId = undefined;
                this.stopHeartbeat();
                console.warn('[Heartbeat]: lease has expired, stopping heartbeat');
                return;
            }

            console.debug('[Heartbeat][Error]: lease still valid, will retry heartbeat');
        }
    }

    static async releaseSeat(releaseReason: ReleaseReason): Promise<void> {
        try {
            if (!this.selectedLicenseId) return;

            const response: Response = await this.fetchWithPrep(`${APPWRITE_API}/releaseseat`, {
                method: 'POST',
                headers: this.headers,
                credentials: 'include',
                body: JSON.stringify({
                    userId: this.userId,
                    sessionId: this.sessionId,
                    licenseId: this.selectedLicenseId,
                    deviceId: this.headers['device-id'],
                    detectedIp: this.detectedIp,
                    releaseReason: releaseReason ?? 'unknown'
                })
            });
            this.logResponseStatus('releaseSeat', response);
            const data: unknown = await response.json();
            const rawBody: SuccessFlagResponse = data as SuccessFlagResponse;
            if (!rawBody?.success) throw new Error(toErrorPayload(rawBody?.error)?.message || 'Release failed');
        } finally {
            this.selectedLicenseId = undefined;
            this.stopRefreshLoop();
            this.stopHeartbeat();
            this.unbindListeners();
        }
    }

    static ensureListenersBound(): void {
        if (this.listenersBound) return;
        this.listenersBound = true;

        window.addEventListener('online', this.onOnline, { passive: true });
        window.addEventListener('offline', this.onOffline, { passive: true });
        window.addEventListener('beforeunload', this.beforeUnload, { passive: true });
    }

    static unbindListeners(): void {
        if (!this.listenersBound) return;
        window.removeEventListener('online', this.onOnline);
        window.removeEventListener('offline', this.onOffline);
        window.removeEventListener('beforeunload', this.beforeUnload);
        this.listenersBound = false;
    }

    static async setDeviceId(deviceId: string): Promise<void> {
        // Set in memory for current session and ensure it's included in headers for API calls
        this.headers['device-id'] = deviceId;

        // Persist device ID to for future sessions
        const result = await this.saveDeviceId();
        if (!result) console.warn('[LicenseService]: Failed to persist device ID');
    }

    private static onOnline = (): void => {
        this.onNetworkChange?.(true);
    };

    private static onOffline = (): void => {
        this.onNetworkChange?.(false);
    };

    private static beforeUnload = (): void => {
        console.debug('[Heartbeat]: stopped on before unload');
        if (!this.selectedLicenseId) return;
        try {
            const body: string = JSON.stringify({
                userId: this.userId,
                sessionId: this.sessionId,
                licenseId: this.selectedLicenseId,
                deviceId: this.headers['device-id'],
                detectedIp: this.detectedIp,
                appVersion: window.LVGL_EDITOR_VERSION || 'unknown',
                platform: window?.platform?.getHostPlatform() || 'unknown'
            });
            const url: string = new URL('/releaseseat', APPWRITE_API).toString();
            navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        } catch (error: unknown) {
            console.error('[LicenseService]: Error in LicenseService.beforeUnload:', error);
        }
    };

    static clear() {
        this.setError(null);
        this.selectedLicenseId = undefined;
        this.userId = '';
        this.sessionId = '';
        this.stopRefreshLoop();
    }
}
